export type CorrelationMeasureId =
  | 'n'
  | 'meanX'
  | 'meanY'
  | 'stdX'
  | 'stdY'
  | 'r'
  | 'rSquared'
  | 'slope'
  | 'intercept'
  | 'equation';

export interface CorrelationMeasureDef {
  readonly id: CorrelationMeasureId;
  readonly label: string;
  /** KaTeX inline symbol, e.g. "$r$" */
  readonly symbolLatex: string;
  /** One-line plain-text description shown in the tooltip */
  readonly description: string;
  /** Symbolic formula in KaTeX */
  readonly formulaLatex: string;
}

export interface CorrelationMeasureResult {
  readonly id: CorrelationMeasureId;
  readonly value: number | string | null;
  readonly formattedValue: string;
  /** Formula with actual data plugged in, result highlighted */
  readonly pluggedInLatex: string;
}

/** Colors for each measure — matches chart overlay conventions. */
export const CORRELATION_MEASURE_COLORS: Readonly<Record<CorrelationMeasureId, string>> = {
  n: '#9ca3af', // gray-400
  meanX: '#f43f5e', // rose-500
  meanY: '#f43f5e', // rose-500
  stdX: '#06b6d4', // cyan-500
  stdY: '#06b6d4', // cyan-500
  r: '#6366f1', // indigo-500
  rSquared: '#8b5cf6', // violet-500
  slope: '#ef4444', // red-500
  intercept: '#ef4444', // red-500
  equation: '#ef4444', // red-500
};

export const CORRELATION_MEASURE_DEFS: readonly CorrelationMeasureDef[] = [
  {
    id: 'n',
    label: 'Count (pairs)',
    symbolLatex: '$n$',
    description: 'The number of data pairs (x, y) in the dataset.',
    formulaLatex: '$n = \\text{number of data pairs}$',
  },
  {
    id: 'meanX',
    label: 'Mean of x',
    symbolLatex: '$\\bar{x}$',
    description: 'The arithmetic mean of all x-values.',
    formulaLatex: '$\\bar{x} = \\dfrac{\\sum x_i}{n}$',
  },
  {
    id: 'meanY',
    label: 'Mean of y',
    symbolLatex: '$\\bar{y}$',
    description: 'The arithmetic mean of all y-values.',
    formulaLatex: '$\\bar{y} = \\dfrac{\\sum y_i}{n}$',
  },
  {
    id: 'stdX',
    label: 'Std. Dev. of x',
    symbolLatex: '$S_x$',
    description: 'The standard deviation of the x-values, measuring their spread.',
    formulaLatex: '$S_x = \\sqrt{\\dfrac{\\sum (x_i - \\bar{x})^2}{n}}$',
  },
  {
    id: 'stdY',
    label: 'Std. Dev. of y',
    symbolLatex: '$S_y$',
    description: 'The standard deviation of the y-values, measuring their spread.',
    formulaLatex: '$S_y = \\sqrt{\\dfrac{\\sum (y_i - \\bar{y})^2}{n}}$',
  },
  {
    id: 'r',
    label: "Pearson's r",
    symbolLatex: '$r$',
    description:
      "Pearson's product-moment correlation coefficient. Measures the strength and direction of the linear relationship.",
    formulaLatex:
      '$r = \\dfrac{n\\sum x_i y_i - \\sum x_i \\sum y_i}{\\sqrt{(n\\sum x_i^2 - (\\sum x_i)^2)(n\\sum y_i^2 - (\\sum y_i)^2)}}$',
  },
  {
    id: 'rSquared',
    label: 'R-squared',
    symbolLatex: '$r^2$',
    description:
      'The coefficient of determination. The proportion of variance in y explained by the linear relationship with x.',
    formulaLatex: '$r^2 = (r)^2$',
  },
  {
    id: 'slope',
    label: 'Slope',
    symbolLatex: '$a$',
    description:
      'The gradient of the regression line y on x. For each unit increase in x, y changes by a units on average.',
    formulaLatex: '$a = \\dfrac{n\\sum x_i y_i - \\sum x_i \\sum y_i}{n\\sum x_i^2 - (\\sum x_i)^2}$',
  },
  {
    id: 'intercept',
    label: 'Y-intercept',
    symbolLatex: '$b$',
    description: 'The y-intercept of the regression line. The predicted value of y when x = 0.',
    formulaLatex: '$b = \\bar{y} - a\\bar{x}$',
  },
  {
    id: 'equation',
    label: 'Regression equation',
    symbolLatex: '$\\hat{y}$',
    description: 'The least-squares regression line y on x. Used to predict y from a given x-value.',
    formulaLatex: '$\\hat{y} = ax + b$',
  },
];
